import { z } from 'zod/v4';

import { getNormalizedFileName } from '../export/exportFileName';
import { assertSuccessfulResponseGenericErrorCode } from '../http/assertSuccessfulResponseGenericErrorCode';
import { endpoint } from '../http/endpoint';
import { AssemblyIdRuntype, PcbDesignIdRuntype, PcbIdRuntype } from '../idTypes';
import {
    AnnularRingsDescriptionRuntype,
    DistanceDescriptionRuntype,
    HoleListRuntype,
    MeasurementStatusRuntype,
    TraceWidthDescriptionRuntype,
} from './dfmBackendTypes';
import { PcbAlertRuntype } from './pcbAlertBackendTypes';
import {
    OutlineRuntype,
    PcbApprovalStateRuntype,
    PcbEmissionsResponseRuntype,
    PcbErrorRuntype,
    PcbServerErrorType,
    PCBV2Runtype,
    PCBV2SpecificationRuntype,
    PCBV2UpdateRuntype,
    SetOutlineRuntype,
} from './pcbBackendTypes';
import { PCBV2SpecificationCapabilitiesRuntype } from './pcbCapabilityBackendTypes';
import { ExtractionResultsPostRuntype, ExtractionResultsRuntype } from './pcbExtractionResultBackendTypes';
import { PCBJournalEventEnvelopeRuntype, PCBJournalTimelineRuntype } from './pcbJournalBackendTypes';
import {
    ApiFileLifecycleRuntype,
    FileLifecycleStageRuntype,
    IndividualAssemblyLifecycleStageRuntype,
    LifecycleSummaryRuntype,
} from './pcbLifecycleBackendTypes';
import {
    PanelDetailsPostRuntype,
    PanelPatchRuntype,
    PanelPostRuntype,
    PanelRenderRequestRuntype,
    PanelRuntype,
} from './pcbPanelBackendTypes';
import { PCBGraphicsArray, PCBGraphicsResponse, PCBGraphicsResponseRuntype } from './pcbSvgBackendTypes';

/** Which central material catalog a system-admin CSV import targets. Mirrors the backend `MaterialKind` enum. */
export const MaterialKindRuntype = z.enum(['base', 'soldermask']);
export type MaterialKind = z.infer<typeof MaterialKindRuntype>;

/** How a central-material CSV import is applied. Mirrors the backend `MaterialImportMode` enum. */
export const MaterialImportModeRuntype = z.enum(['replace-all', 'upsert']);
export type MaterialImportMode = z.infer<typeof MaterialImportModeRuntype>;

export const MaterialImportRequestRuntype = z.object({
    kind: MaterialKindRuntype,
    mode: MaterialImportModeRuntype,
    csv: z.string(),
});
export type MaterialImportRequest = z.infer<typeof MaterialImportRequestRuntype>;

export const MaterialImportRowErrorRuntype = z.object({
    line: z.number(),
    message: z.string(),
});
export type MaterialImportRowError = z.infer<typeof MaterialImportRowErrorRuntype>;

export const MaterialImportResultRuntype = z.object({
    kind: MaterialKindRuntype,
    mode: MaterialImportModeRuntype,
    totalRows: z.number(),
    imported: z.number(),
    deleted: z.number(),
    errors: z.array(MaterialImportRowErrorRuntype),
});
export type MaterialImportResult = z.infer<typeof MaterialImportResultRuntype>;

export const pcbEndpoints = {
    'POST /ems/pcb/materials/import': endpoint({
        description:
            'System-admin CSV import of a central PCB material catalog (base | soldermask), in replace-all or upsert mode. Internal-only.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: MaterialImportRequestRuntype,
        responseBody: MaterialImportResultRuntype,
    }),

    'POST /ems/pcb/v2/pcbs': endpoint({
        description: 'Creates a new PCB',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            name: z.string(),
            orderId: z.string(),
            withoutFiles: z.boolean().optional(),
        }),
        responseBody: PCBV2Runtype,
    }),

    'GET /ems/pcb/v2/pcbs/:pcbId': endpoint({
        description: 'Find a PCB by ID',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: PCBV2Runtype,
    }),

    'GET /ems/pcb/v2/pcbs/:pcbId/capabilities': endpoint({
        description: 'The list of allowed values and setting for every field in the PCB.',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.undefined(),
        // `materialVendor` / `soldermaskMaterialVendor` override the vendor used to filter
        // `layerStack.materialNumber.allowed` / `board.soldermaskMaterialNumber.allowed` respectively, so the
        // spec form can refresh the linked material-number options for the in-progress (unsaved) vendor
        // without first saving the spec. `materialNumber` / `soldermaskMaterialNumber` override the selected
        // material so the implied/derived fields (e.g. soldermask color) are recomputed live for the in-progress
        // material — again without first saving.
        queryParams: z.object({
            materialVendor: z.string().optional(),
            materialNumber: z.string().optional(),
            soldermaskMaterialVendor: z.string().optional(),
            soldermaskMaterialNumber: z.string().optional(),
        }),
        responseBody: PCBV2SpecificationCapabilitiesRuntype,
    }),

    'GET /ems/pcb/v2/pcbs/:pcb/specifications': endpoint({
        description: 'Lists the specifications of a PCB.',
        pathParams: z.object({ pcb: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(PCBV2SpecificationRuntype),
    }),

    'GET /ems/pcb/v2/pcbs/:pcb/specifications/:specification': endpoint({
        description: 'Gets a single PCB specification by name or id.',
        pathParams: z.object({ pcb: PcbIdRuntype, specification: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PCBV2SpecificationRuntype,
    }),

    'GET /ems/pcb/v2/pcbs/:pcb/journal': endpoint({
        description:
            'Internal-staff debugging read: raw journal-event timeline (descriptors) for a PCB and related entities.',
        pathParams: z.object({ pcb: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PCBJournalTimelineRuntype,
    }),

    'GET /ems/pcb/v2/pcbs/:pcb/journal/events/:entityType/:entityId/:seq': endpoint({
        description: 'Internal-staff debugging read: decoded payload of a single journal event.',
        pathParams: z.object({ pcb: PcbIdRuntype, entityType: z.string(), entityId: z.string(), seq: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PCBJournalEventEnvelopeRuntype,
    }),

    // Stackrate AssemblyService lifecycle reads (PCB domain). Fetch lifecycle state
    // granularly — summary first, then board/file stages — instead of pulling the whole
    // PCB object. `version` is the Stackrate version (== the PCB id).
    'GET /assembly/assemblies/:assembly/versions/:version/lifecyclesummary': endpoint({
        description:
            'Compact lifecycle summary for an assembly version: board (version) stages, aggregates, and per-file stages. The cheap first call when inspecting a PCB.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: LifecycleSummaryRuntype,
    }),
    'GET /assembly/assemblies/:assembly/versions/:version/lifecycles': endpoint({
        description: 'All lifecycle stages for an assembly version, keyed by stage name.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        queryParams: z.object({ detail: z.boolean().optional() }),
        requestBody: z.undefined(),
        responseBody: z.record(z.string(), ApiFileLifecycleRuntype),
    }),
    'GET /assembly/assemblies/:assembly/versions/:version/lifecycles/:lcname': endpoint({
        description: 'A single board-level lifecycle stage for an assembly version, by stage name.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype, lcname: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: IndividualAssemblyLifecycleStageRuntype,
    }),
    'GET /assembly/assemblies/:assembly/lifecycles': endpoint({
        description: 'Lifecycle stages for an assembly, optionally pinned to a specific version and/or point in time.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype }),
        queryParams: z.object({ time: z.number().optional(), version: PcbIdRuntype.optional() }),
        requestBody: z.undefined(),
        responseBody: z.record(z.string(), ApiFileLifecycleRuntype),
    }),
    'GET /assembly/assemblies/:assembly/versions/:version/files/:file/lifecycles': endpoint({
        description: 'Per-file lifecycle stages for a single file in an assembly version.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype, file: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(FileLifecycleStageRuntype),
    }),
    'GET /assembly/assemblies/:assembly/versions/:version/files/:file/lifecycles/:lcname': endpoint({
        description: 'A single per-file lifecycle stage, by stage name.',
        pathParams: z.object({
            assembly: PcbDesignIdRuntype,
            version: PcbIdRuntype,
            file: z.string(),
            lcname: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: FileLifecycleStageRuntype,
    }),

    'PUT /ems/pcb/v2/pcbs/:pcb/specifications/:specification': endpoint({
        description: 'Updates a PCB specification',
        pathParams: z.object({ pcb: PcbIdRuntype, specification: z.string() }),
        queryParams: z.object({ removeUndefined: z.boolean().optional() }),
        requestBody: PCBV2UpdateRuntype,
        responseBody: PCBV2SpecificationRuntype,
        invalidates: [
            'GET /ems/pcb/v2/pcbs/:pcbId/capabilities',
            'GET /ems/pcb/v2/pcbs/:pcbId',
            'GET /assemblies/:assemblyId/state',
            'POST /rfqs/:rfqId/customer-portal',
            'GET /custom-part-alerts/:partId',
            'GET /panels',
        ],
    }),

    'PATCH /pcb-sides': endpoint({
        description: 'Set PCB Sides for the assembly',
        pathParams: z.undefined(),
        requestBody: z.object({ pcb_sides: z.number() }),
        queryParams: z.object({ assembly_id: AssemblyIdRuntype }),
        responseBody: z.null(),
    }),

    'GET /pcb-sides': endpoint({
        description: 'Get PCB Sides for the assembly',
        pathParams: z.undefined(),
        requestBody: z.undefined(),
        queryParams: z.object({ assembly_id: AssemblyIdRuntype }),
        responseBody: z.object({ pcb_sides: z.number() }),
    }),

    'GET /pcb/:pcbId/graphics': endpoint({
        description: 'Get all the PCB Graphics.',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: PCBGraphicsResponseRuntype,
        handleResponse: async (response, _) => {
            if (response.ok) {
                const json = await response.json();

                const graphicResponse: PCBGraphicsResponse = {
                    status: 'ok',
                    data: json as PCBGraphicsArray,
                };

                return graphicResponse;
            }

            if (response.status === 404) {
                const json = await response.json();

                try {
                    const checked = PcbErrorRuntype.parse(json);
                    if (checked.error.type === PcbServerErrorType.MissingLayerstack) {
                        const errorResponse: PCBGraphicsResponse = {
                            status: 'error',
                            error: PcbServerErrorType.MissingLayerstack,
                        };
                        return errorResponse;
                    } else if (checked.error.type === PcbServerErrorType.MissingOutline) {
                        const errorResponse: PCBGraphicsResponse = {
                            status: 'error',
                            error: PcbServerErrorType.MissingOutline,
                        };
                        return errorResponse;
                    }
                } catch {
                    // Not a known PCB error: throw the generic one below.
                }
            }

            throw Error(`Failed to GET /pcb/:pcbId/graphics: ${response.status}`);
        },
    }),

    'GET /ems/renderer/layerstacks/versions/:pcbId': endpoint({
        description: 'Get layerstack version',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.string().nullable(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return response.text();
            }
            if (response.status === 404) {
                return null;
            }
            throw Error(`Unexpected response status ${response.status}`);
        },
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:versionId/distances': endpoint({
        description: 'Get all board distances between traces from renderer service',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            max: z.number().optional(),
            files: z.array(z.string()).optional(),
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        responseBody: z.record(z.string(), z.array(DistanceDescriptionRuntype)),
        arrayFormat: 'repeat',
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:versionId/files/:fileName/distances': endpoint({
        description: 'Get all distances for a specific file from renderer service',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
            fileName: z.string(),
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            max: z.number().optional(),
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        responseBody: z.array(DistanceDescriptionRuntype),
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:versionId/files/:fileName/tracewidths': endpoint({
        description: 'Get all trace widths for a specific file from renderer service',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
            fileName: z.string(),
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        responseBody: z.array(
            z.object({
                element: z.number(),
                width: z.number(),
                trace: z.string().optional(),
                netId: z.string().optional(),
            }),
        ),
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:versionId/tracewidths': endpoint({
        description: 'Get trace widths for multiple files from renderer service (bulk)',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            files: z.array(z.string()).optional(),
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        responseBody: z.record(z.string(), z.array(TraceWidthDescriptionRuntype)),
        arrayFormat: 'repeat',
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:versionId/annularRings': endpoint({
        description: 'Get all annular ring measurements for an assembly version from renderer service',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            drillType: z.string().optional(),
            page: z.number().optional(),
            pagesize: z.number().optional(),
        }),
        responseBody: AnnularRingsDescriptionRuntype,
    }),

    'PUT /ems/renderer/assemblies/:assemblyId/versions/:versionId/measurements/status': endpoint({
        description: 'Set the status of distance, trace width and annular ring measurements',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
        }),
        queryParams: z.undefined(),
        requestBody: z.object({
            status: MeasurementStatusRuntype,
            distances: z.array(z.string()),
            tracewidths: z.array(z.string()),
            annularRings: z.array(z.string()),
        }),
        responseBody: z.string(),
    }),

    'GET /ems/renderer/assemblies/:assemblyId/versions/:versionId/files/graphics': endpoint({
        description: 'Get SVG graphics with element IDs for a specific file from renderer service',
        pathParams: z.object({
            assemblyId: PcbDesignIdRuntype,
            versionId: PcbIdRuntype,
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            file: z.string(),
        }),
        responseBody: z.record(
            z.string(),
            z.object({
                viewbox: z
                    .object({
                        min: z.object({ x: z.number(), y: z.number() }),
                        max: z.object({ x: z.number(), y: z.number() }),
                    })
                    .optional(),
                defs: z.array(z.any()).optional(),
                paths: z.array(z.any()).optional(),
            }),
        ),
    }),

    'GET /ems/pcb/v2/pcbs/:pcbId/token': endpoint({
        description: 'Get websocket token',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.object({ authToken: z.string() }),
    }),

    'POST /panels': endpoint({
        description: 'Creates a new panel',
        pathParams: z.undefined(),
        requestBody: PanelPostRuntype,
        queryParams: z.undefined(),
        responseBody: PanelRuntype,
        invalidates: ['GET /panels'],
    }),

    'PATCH /panels/:panelId': endpoint({
        description: 'Updates a panel (used mostly for GlobalPanel)',
        pathParams: z.object({
            panelId: z.string(),
        }),
        requestBody: PanelPatchRuntype,
        queryParams: z.undefined(),
        responseBody: PanelRuntype,
        invalidates: ['GET /panels'],
    }),

    'GET /panels': endpoint({
        description: 'Gets the created panel',
        pathParams: z.undefined(),
        requestBody: z.undefined(),
        queryParams: z.object({
            pcb_id: PcbIdRuntype,
            assembly_id: AssemblyIdRuntype,
        }),
        responseBody: PanelRuntype.nullable(),
    }),

    'POST /panels/generate': endpoint({
        description: 'Get values',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            pcb_id: PcbIdRuntype,
            amount: z.number(),
            panel_preferences_id: z.string().optional(),
        }),
        responseBody: PanelDetailsPostRuntype,
    }),

    'POST /pcb/panel/render': endpoint({
        description: 'Get panel renderer',
        pathParams: z.undefined(),
        queryParams: z.object({
            pcbWidth: z.number(),
            pcbHeight: z.number(),
        }),
        requestBody: PanelRenderRequestRuntype,
        responseBody: z.string().nullable(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                return response.text();
            }
            if (response.status === 404) {
                return null;
            }
            throw Error(`Unexpected response status ${response.status}`);
        },
    }),

    'GET /pcb/:pcbId/panel/download-pdf': endpoint({
        description:
            'Renders the panel specification as a PDF. Query params are camelCase because this is the Scala PCB service, not the Rust backend. `projectName` names the downloaded file and must be omitted rather than sent empty.',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        queryParams: z.object({ panelId: z.string(), projectName: z.string().optional() }),
        requestBody: z.undefined(),
        responseBody: z.object({ fileName: z.string(), blob: z.instanceof(Blob) }),
        handleResponse: async (response, endpoint) => {
            await assertSuccessfulResponseGenericErrorCode(response, endpoint);
            return { fileName: getNormalizedFileName(response), blob: await response.blob() };
        },
        actionPermission: () => 'CanViewPcb',
    }),

    'DELETE /panels/:panelId': endpoint({
        description: 'Deletes a panel',
        pathParams: z.object({
            panelId: z.string(),
        }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.object({
            deleted: z.number(),
        }),
        invalidates: ['GET /panels'],
    }),

    'GET /assemblies/:assemblyId/pcb/:pcbId/emissions': endpoint({
        description: 'Gets the emissions range for a PCB',
        pathParams: z.object({
            assemblyId: AssemblyIdRuntype,
            pcbId: PcbIdRuntype,
        }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: PcbEmissionsResponseRuntype,
        invalidates: ['GET /assemblies/:assemblyId/descendants-summary'],
    }),

    'GET /emissions/pcb/:pcbId': endpoint({
        description: 'Gets the emissions range for a PCB',
        pathParams: z.object({
            pcbId: PcbIdRuntype,
        }),
        requestBody: z.undefined(),
        queryParams: z.object({
            assembly_id: AssemblyIdRuntype.optional(),
        }),
        responseBody: PcbEmissionsResponseRuntype,
    }),

    'GET /pcb-pdf-specification-extraction': endpoint({
        description: 'Gets the PDF extracted values',
        pathParams: z.undefined(),
        requestBody: z.undefined(),
        queryParams: z.object({
            pcb_id: PcbIdRuntype,
        }),
        responseBody: ExtractionResultsRuntype.nullable(),
    }),

    'POST /pcb-pdf-specification-extraction': endpoint({
        description: 'Saves the PDF extracted values',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ExtractionResultsPostRuntype,
        responseBody: ExtractionResultsRuntype,
        invalidates: ['GET /pcb-pdf-specification-extraction'],
    }),

    'DELETE /pcb-pdf-specification-extraction/:pcbId': endpoint({
        description: 'Deletes a pdf specification extraction',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.object({
            deleted: z.number(),
        }),
        invalidates: ['GET /pcb-pdf-specification-extraction'],
    }),

    'GET /pcb-approval-state': endpoint({
        description: 'Gets the approval state for a PCB',
        pathParams: z.undefined(),
        requestBody: z.undefined(),
        queryParams: z.object({ pcb_id: PcbIdRuntype }),
        responseBody: PcbApprovalStateRuntype.nullable(),
    }),

    'POST /pcb-approval-state/bulk': endpoint({
        description: 'Gets the approval states for multiple PCBs',
        pathParams: z.undefined(),
        requestBody: z.object({
            ids: z.array(z.string()),
        }),
        queryParams: z.undefined(),
        responseBody: z.object({
            items: z.array(PcbApprovalStateRuntype),
        }),
    }),

    'PATCH /pcb-approval-state/:pcbId': endpoint({
        description: 'Updates the approval state for a PCB',
        pathParams: z.object({ pcbId: PcbIdRuntype }),
        requestBody: PcbApprovalStateRuntype,
        queryParams: z.undefined(),
        responseBody: PcbApprovalStateRuntype,
        invalidates: [
            'GET /pcb-approval-state',
            'GET /assemblies/:assemblyId/state',
            'POST /assemblies/:id/pcb/:pcbId/offer-state',
        ],
    }),

    'GET /custom-part-alerts/:partId': endpoint({
        description: 'Gets the custom part alerts for a custom part',
        pathParams: z.object({ partId: z.string() }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.array(PcbAlertRuntype),
    }),

    'POST /custom-part-alerts/dismiss/:alertId': endpoint({
        description: 'Dismisses a custom part alert',
        pathParams: z.object({ alertId: z.string() }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.null(),
        invalidates: ['GET /custom-part-alerts/:partId'],
    }),

    'GET /ems/pcb/:assembly/versions/:version/outlines': endpoint({
        description: 'Get outline candidates for a PCB assembly version',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.object({ withGraphic: z.boolean().optional() }),
        responseBody: z.array(OutlineRuntype),
    }),

    'GET /ems/pcb/:assembly/versions/:version/outline': endpoint({
        description: 'Get the chosen outline for a PCB assembly version',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.object({ withGraphic: z.boolean().optional() }),
        responseBody: OutlineRuntype,
    }),

    'GET /ems/pcb/:assembly/versions/:version/drills': endpoint({
        description: 'Get reconciled drill hole lists for a PCB assembly version',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        requestBody: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.array(HoleListRuntype),
    }),

    'POST /ems/pcb/:assembly/versions/:version/outline': endpoint({
        description: 'Set the outline for a PCB assembly version',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype }),
        requestBody: SetOutlineRuntype,
        queryParams: z.undefined(),
        responseBody: z.null(),
        handleResponse: async (response, _) => {
            if (response.ok) {
                // Backend returns empty string for successful Done responses
                return null;
            }
            throw Error(`Failed to set outline: ${response.status}`);
        },
        invalidates: [
            'GET /ems/pcb/:assembly/versions/:version/outlines',
            'GET /ems/pcb/v2/pcbs/:pcbId',
            'GET /pcb/:pcbId/graphics',
        ],
    }),
};
